import { Body, Controller, Delete, Get, HttpCode, Param, Patch, Post, Query, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import {
  CategoryListResponse,
  CategorySummary,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@cart/shared-types';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard } from '../auth/auth.guard';
import { CategoriesService } from './categories.service';

@ApiTags('Categories')
@ApiSecurity('store-id')
@Controller('categories')
export class CategoriesController {
  constructor(private readonly categoriesService: CategoriesService) {}

  @Get()
  @ApiOperation({ summary: 'List categories — omit parentId for all, parentId=root for top-level, parentId=<id> for children' })
  @ApiResponse({ status: 200, description: 'Returns category list' })
  list(@Query('parentId') parentId?: string): Promise<CategoryListResponse> {
    return this.categoriesService.list(parentId);
  }
}

@ApiTags('Admin · Categories')
@ApiBearerAuth()
@ApiSecurity('store-id')
@UseGuards(AuthGuard, AdminAccessGuard)
@Controller('admin/categories')
export class AdminCategoriesController {
  constructor(private readonly categoriesService: CategoriesService) {}

  @Post()
  @ApiOperation({ summary: 'Create a new category (admin)' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['name', 'slug'],
      properties: {
        name: { type: 'string', example: 'Jackets' },
        slug: { type: 'string', example: 'jackets', description: 'URL-safe lowercase slug' },
        parentId: { type: 'string', nullable: true, example: null, description: 'Parent category ID for nested categories' },
        level: { type: 'integer', example: 0, description: 'Nesting depth (0 = top-level)' },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Category created' })
  @ApiResponse({ status: 400, description: 'Invalid slug format' })
  @ApiResponse({ status: 409, description: 'Slug already exists' })
  create(@Body() body: CreateCategoryRequest): Promise<CategorySummary> {
    return this.categoriesService.create(body);
  }

  @Patch(':id')
  @ApiOperation({ summary: 'Update a category (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiBody({
    schema: {
      type: 'object',
      properties: {
        name: { type: 'string', example: 'Outerwear' },
        parentId: { type: 'string', nullable: true, example: null, description: 'Parent category ID (null to make top-level)' },
        level: { type: 'integer', example: 0, description: 'Nesting depth (0 = top-level)' },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Category updated' })
  @ApiResponse({ status: 404, description: 'Category not found' })
  update(
    @Param('id') categoryId: string,
    @Body() body: UpdateCategoryRequest
  ): Promise<CategorySummary> {
    return this.categoriesService.update(categoryId, body);
  }

  @Delete(':id')
  @HttpCode(204)
  @ApiOperation({ summary: 'Hard-delete a category (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 204, description: 'Category deleted permanently' })
  @ApiResponse({ status: 404, description: 'Category not found' })
  delete(@Param('id') categoryId: string): Promise<void> {
    return this.categoriesService.delete(categoryId);
  }
}
